import React, { useEffect, useMemo, useRef, useState } from "react";
import useURLQueryParams from "../hooks/useURLQueryParams";
import { useRouter } from "next/router";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { StoreType } from "../redux/StoreType";
import {
  GET_APP_STATE,
  SET_IS_LOADING_FILE,
  SET_IS_PRINT_SERVICE_UNAVAILABLE,
  SET_IS_VENDOR,
} from "../redux/slices/appSlice";
import { GET_SOURCE_STATE, SET_SOURCE } from "../redux/slices/sourceSlice";
import {
  GET_USER_STATE,
  SET_USER_VENDOR_ADMIN_ROLE,
} from "../redux/slices/userSlice";
import { GET_VENDOR_IMPERSONATION_STATE } from "../redux/slices/impersonationSlice";
import { useViewport } from "../hooks/useViewport";
import { useVehiclesDetailsPageData } from "../hooks/useVehiclesDetailsPageData.hook";
import { useAuth } from "@bca-auth0/bca-auth0-provider";
import { fetchVehicleDetails } from "../pages/lot/[vrm]";
import hasUserRole from "../utilities/userRole.functions";
import UserRoles from "../utilities/userRoles";
import Layout from "../components/Layout";
import Head from "next/head";
import { FeatureFlagProvider } from "bca-feature-flag-provider";
import { FEATURE_FLAGS } from "../components/constants/featureFlags";
import Impersonation from "../components/impersonation/Impersonation";
import { OverlayProvider } from "react-aria";
import BatteryGradeSlideout from "../components/BatteryGradeSlideout";
import pushBatteryGradeData from "../analytics/pushBatteryGradeData";
import {
  Banner,
  Box,
  LinkButton,
  ResponsiveLayout,
  Snackbar,
  Typography,
} from "@bca/ui-library";
import HPIErrorBanner from "../components/hpi/HPIErrorBanner";
import getStringFrom from "../helpers/getStringFrom";
import BackToResultsStrip from "../components/backToResults/BackToResultsStrip";
import useSourcePage from "../hooks/useSourcePage";
import VersionNavigationBanner from "../components/versionNavigationBanner/VersionNavigationBanner";
import ImageGallery from "../components/ImageGallery";
import VehicleInteraction from "../components/VehicleInteraction";
import ClickableImage from "../components/ClickableImage";
import { useStateRef } from "../hooks/customHooks";
import { addDuplicates } from "../helpers/addDuplicates";
import {
  GreySectionRow,
  pushImageZoomToAnalytics,
  StyledResponsiveLayout,
  UsabillaContainer,
} from "./VehicleDetailsIDP";
import { Image } from "../vehicleDetails/vehicle-details-dto.type";
import ImageZoomModal from "../components/ImageZoomModal";
import { VehicleDetailsProps } from "../vehicleDetails/vehicle-details-props.type";
import WarrantedMileage from "../components/WarrantedMileage";
import Owners from "../components/Owners";
import ConditionReport from "../components/ConditionReport";
import BatteryReport from "../components/BatteryReport";
import MechanicalReport from "../components/MechanicalReport";
import HPISpecCheck from "../components/HPISpecCheck";
import GuidePrice from "../components/GuidePrice";
import SalvageDeclarations from "../components/salvageDeclarations/SalvageDeclarations";
import ValuationPrice from "../components/BCAValuation/ValuationPrice";
import useValuationEndpoint from "../hooks/useValuationEndpoint.hook";
import SaleInformation from "../components/SaleInformation";
import BuyingOptions from "../components/buyingOptions/BuyingOptions";
import VehicleSpecification from "../components/VehicleSpecification";
import ConditionAndMechanicalReports from "../components/ConditionAndMechanicalReports";
import HPISpecCheckTable from "../components/HPISpecCheckTable";
import PricingGuide from "../components/PricingGuide";
import ValuationModal from "../components/BCAValuation/ValuationModal";
import LotNavigationBanner from "../components/lotNavigationBanner/LotNavigationBanner";
import VehicleNotFound from "../components/VehicleNotFound";
import VehicleDetailsSkeleton from "../components/VehicleDetailsSkeleton";
import { getVehicleDetails } from "../vehicleDetails/getVehicleDetails";
import { MechanicalItemReportAnalytics } from "../analytics/MechanicalItemReportAnalytics";
import { getAnalyticsPageVariant } from "../analytics/AnalyticsUtils";
import pushPageView from "../analytics/pushPageView";

interface VehicleDetailsAuth0Props {
  isToUseAuthZero: boolean;
  authLogoutUrl?: string;
  impersonatingUser?: string;
  vrm: string;
  appConfigurationUrl: string;
  fromSearch: string | null;
  resolvedUrl: string;
  isAdobeLaunchLoaded?: boolean;
}

// Helper function to create analytics data
const createAnalyticsData = (vehicleDetails: any, fallbackVrm: string) => {
  if (!vehicleDetails) return null;

  const {
    saleDetails: { saleCode, endDate, location, startDate, saleTypeName },
    vin,
    vrm,
    lotNumber,
    hpi,
    images,
    mechanicalReport: mechanicalReportAnalyticsData,
    vehicleType,
    additionalInformation,
    analyticsProps = {},
  } = vehicleDetails;

  return {
    vin,
    saleCode,
    vrm: vrm || fallbackVrm,
    lotNumber,
    location,
    startDate,
    endDate,
    saleTypeName,
    vehicleId: analyticsProps?.vehicleId,
    hpi: Boolean(hpi),
    images,
    mechanicalReportAnalyticsData,
    vehicleType,
    evBattery: additionalInformation?.evBattery,
  };
};

const VehicleDetailsAuth0: React.FC<VehicleDetailsAuth0Props> = (props) => {
  // ========================================
  // HOOKS & ROUTING
  // ========================================
  const { vdr } = useURLQueryParams();
  const router = useRouter();
  const { source } = router.query;
  const viewportWidth = useViewport(100);
  const { shouldRestrict } = useVehiclesDetailsPageData(vdr);
  const { isVPSoldSourcePage, isVPExceptSoldPageOrTransportTab } =
    useSourcePage();

  // ========================================
  // AUTH0 & AUTHENTICATION
  // ========================================
  const redirectUrl = typeof window !== "undefined" ? window.location.href : "";
  const { isAuthenticated, isLoading, accessToken, authUser, idToken } =
    useAuth(!props.isToUseAuthZero, redirectUrl);

  // ========================================
  // REDUX STATE & DISPATCH
  // ========================================
  const dispatch = useDispatch();
  const {
    app: { isLoadingFile, isPrintServiceUnavailable },
    source: { urlSource },
    user: { isVendorAdmin },
    impersonation: { isVendorImpersonationSet },
  }: StoreType = useSelector(
    (state: StoreType) => ({
      app: GET_APP_STATE(state).app,
      source: GET_SOURCE_STATE(state).source,
      user: GET_USER_STATE(state).user,
      impersonation: GET_VENDOR_IMPERSONATION_STATE(state).impersonation,
    }),
    shallowEqual
  );

  // ========================================
  // LOCAL STATE
  // ========================================
  const [vehicleDetails, setVehicleDetails] =
    useState<VehicleDetailsProps | null>(null);
  const [isLoadingVehicleDetails, setIsLoadingVehicleDetails] = useState(false);
  const [hasVehicleDetailsError, setHasVehicleDetailsError] = useState(false);
  const [isSlideoutOpen, setIsSlideoutOpen] = useState<boolean>(false);
  const [showValuationModal, setShowValuationModal] = useState(false);
  const [showImageZoomModal, setShowImageZoomModal] = useState(false);
  const [hpiError, setHpiError] = useState(false);
  const [isTracked, setIsTracked] = useState(false);
  const [isFeaturedVehicle, setIsFeaturedVehicle] = useState(false);

  // ========================================
  // CUSTOM HOOKS
  // ========================================
  const [activeImageIndex, setActiveImageIndex, activeImageIndexRef] =
    useStateRef(0);
  const hasTrackedPageView = useRef(false);
  const { bcaValuationResult, isBCAValuationLoading, isBCAValuationAvailable } =
    useValuationEndpoint(
      props.vrm,
      vehicleDetails?.isProvisional,
      props.isToUseAuthZero,
      accessToken
    );

  // ========================================
  // CONSTANTS & COMPUTED VALUES
  // ========================================
  const SLIDE_SPACING = 10;

  const imagesWithDuplicates = vehicleDetails?.images
    ? vehicleDetails.images.length < 2
      ? vehicleDetails.images
      : addDuplicates(vehicleDetails.images)
    : [];

  // Sale and ownership status
  const isOnSaleAndOwner =
    vehicleDetails?.isVendorOfVehicle && vehicleDetails?.isAllocatedToSale;
  const isOnSaleButNotOwner =
    !vehicleDetails?.isVendorOfVehicle && vehicleDetails?.isAllocatedToSale;
  const isOnSale = isOnSaleButNotOwner || isOnSaleAndOwner;
  const isSalvageVehicle =
    vehicleDetails?.declaration?.salvagedVehicle ?? false;
  const isPostSaleVDP = urlSource === "Purchases" || urlSource === "Invoices";

  // User role checks
  const isAdmin = hasUserRole(authUser?.roles, UserRoles.ADMINISTRATOR);
  const isFeatureVehicle = hasUserRole(
    authUser?.roles,
    UserRoles.FEATURE_VEHICLE
  );

  // Analytics data
  const analyticsData = useMemo(
    () => createAnalyticsData(vehicleDetails, props.vrm),
    [vehicleDetails, props.vrm]
  );

  const mechanicalReportAnalyticsData = useMemo(
    () =>
      MechanicalItemReportAnalytics({
        reportType: vehicleDetails?.mechanicalReport.type,
        categoryMapping: vehicleDetails?.assessment?.categoryMapping,
      }),
    [
      vehicleDetails?.mechanicalReport.type,
      vehicleDetails?.assessment?.categoryMapping,
    ]
  );

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const shouldShowPricing = () => {
    if (isVPExceptSoldPageOrTransportTab) {
      return true;
    }

    const isAnyPriceAvailable = Object.values(
      vehicleDetails?.prices || {}
    ).some((price) => Boolean(price?.value));
    return isAnyPriceAvailable;
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleOpenBatteryGradeSlideout = () => {
    setIsSlideoutOpen(true);
    pushBatteryGradeData(
      {
        action: "open",
        category: "link",
        label: "ev-battery|overlay",
        name: "click",
        section: "primary-information",
        value: "",
      },
      analyticsData
    );
  };

  const handleCloseBatteryGradeSlideout = () => setIsSlideoutOpen(false);

  const handleCloseSnackBar = () => {
    dispatch(SET_IS_LOADING_FILE(false));
  };

  const handleModalImage = (images: Image[], index: number) => {
    setActiveImageIndex(index);
    setShowImageZoomModal(true);
    pushImageZoomToAnalytics(
      images,
      index - 1,
      isVendorAdmin,
      isVendorImpersonationSet
    );
  };

  // ========================================
  // EFFECTS - ANALYTICS UPDATES
  // ========================================
  useEffect(() => {
    // Only track once per page load when all data is available
    if (hasTrackedPageView.current) return;
    if (!props.isAdobeLaunchLoaded) return;
    if (!authUser || !analyticsData || !vehicleDetails) return;
    if (isLoading || isLoadingVehicleDetails) return;

    const hasMechanicalReportData = mechanicalReportAnalyticsData?.length > 0;
    const isVendorWithImpersonation = isVendorAdmin && isVendorImpersonationSet;
    const shouldTrackPageView =
      hasMechanicalReportData && (isVendorWithImpersonation || !isVendorAdmin);

    if (!shouldTrackPageView) return;

    const { ssoId, casId, peep_analyticsid, userType } = authUser;

    pushPageView({
      peep_analyticsid: peep_analyticsid,
      ssoId: ssoId,
      casId: casId,
      userType: userType,
      isVendorAdmin: isVendorAdmin,
      vrm: analyticsData.vrm,
      saleCode: analyticsData.saleCode,
      saleType: analyticsData.saleTypeName,
      lotNumber: analyticsData.lotNumber,
      location: analyticsData.location,
      saleStartDate: analyticsData.startDate,
      saleEndDate: analyticsData.endDate,
      vehicleId: analyticsData.vehicleId,
      hasHpi: analyticsData.hpi,
      images: analyticsData.images,
      mechanicalReportAnalyticsData: mechanicalReportAnalyticsData,
      pageVariant: getAnalyticsPageVariant(vdr),
      vehicleType: analyticsData.vehicleType,
      evBattery: analyticsData.evBattery,
      isSalvageVehicle: isSalvageVehicle,
      vin: analyticsData.vin,
    });

    hasTrackedPageView.current = true;
  }, [
    props.isAdobeLaunchLoaded,
    authUser,
    analyticsData,
    vehicleDetails,
    isLoading,
    isLoadingVehicleDetails,
    isSalvageVehicle,
    isVendorAdmin,
    isVendorImpersonationSet,
    mechanicalReportAnalyticsData,
    vdr,
  ]);

  // ========================================
  // EFFECTS - REDUX UPDATES
  // ========================================
  useEffect(() => {
    dispatch(SET_SOURCE(source));
  }, [source, dispatch]);

  useEffect(() => {
    const isVendorAdminUser = hasUserRole(
      authUser?.roles,
      UserRoles.VENDOR_ADMIN
    );
    dispatch(SET_USER_VENDOR_ADMIN_ROLE(isVendorAdminUser));
  }, [authUser?.roles, dispatch]);

  useEffect(() => {
    dispatch(SET_IS_VENDOR(authUser?.isVendor));
  }, [authUser?.isVendor, dispatch]);

  // ========================================
  // EFFECTS - UI STATE MANAGEMENT
  // ========================================
  useEffect(() => {
    if (
      vehicleDetails?.degradedServices?.length > 0 &&
      vehicleDetails?.degradedServices.includes("InventoryService")
    ) {
      setHpiError(true);
    }
  }, [vehicleDetails?.degradedServices]);

  useEffect(() => {
    if (isPrintServiceUnavailable) {
      const timer = setTimeout(() => {
        dispatch(SET_IS_PRINT_SERVICE_UNAVAILABLE(false));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [dispatch, isPrintServiceUnavailable]);

  useEffect(() => {
    if (vehicleDetails?.images) {
      const newIndex = vehicleDetails.images.length === 1 ? 0 : 2;
      setActiveImageIndex(newIndex);
    }
  }, [vehicleDetails?.images, setActiveImageIndex]);

  useEffect(() => {
    if (vehicleDetails) {
      setIsTracked(vehicleDetails.isTracked ?? false);
    }
  }, [vehicleDetails]);

  useEffect(() => {
    if (vehicleDetails) {
      setIsFeaturedVehicle(vehicleDetails.isFeatured ?? false);
    }
  }, [vehicleDetails]);
  // ========================================
  // EFFECTS - DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingVehicleDetails(true);
      setHasVehicleDetailsError(false);
      try {
        const urlParams = { ...router.query };
        delete urlParams.vrm;

        const vehicleDetailsData = await fetchVehicleDetails(
          accessToken,
          props.vrm,
          urlParams,
          props.impersonatingUser
        );

        if (vehicleDetailsData) {
          setVehicleDetails(getVehicleDetails(vehicleDetailsData));
        }
      } catch (error) {
        console.error("Error fetching vehicle details:", error);
        setHasVehicleDetailsError(true);
      } finally {
        setIsLoadingVehicleDetails(false);
      }
    };

    if (accessToken) {
      fetchData();
    }
  }, [accessToken, router.query, props.impersonatingUser, props.vrm]);

  if (
    (isLoading || isLoadingVehicleDetails || !vehicleDetails) &&
    !hasVehicleDetailsError
  ) {
    return (
      <VehicleDetailsSkeleton
        authUser={authUser}
        idToken={idToken}
        isAuthenticated={isAuthenticated}
        logoutUrl={props.authLogoutUrl}
        isToUseAuthZero={props.isToUseAuthZero}
      />
    );
  }

  if (hasVehicleDetailsError) {
    return (
      <Layout
        user={
          authUser
            ? {
                firstName: authUser.firstName,
                lastName: authUser.lastName || "",
              }
            : undefined
        }
        idToken={idToken}
        isToUseAuthZero={props.isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={props.authLogoutUrl}
      >
        <VehicleNotFound />
      </Layout>
    );
  }

  return (
    <Layout
      user={
        authUser
          ? { firstName: authUser.firstName, lastName: authUser.lastName || "" }
          : undefined
      }
      idToken={idToken}
      isToUseAuthZero={props.isToUseAuthZero}
      isAuthenticated={isAuthenticated}
      logoutUrl={props.authLogoutUrl}
    >
      <Head>
        <title>{vehicleDetails?.description}</title>
      </Head>
      <FeatureFlagProvider
        clientSecret={props.appConfigurationUrl}
        keys={FEATURE_FLAGS}
        context={{ userId: authUser.customerNumber }}
      >
        {isVendorAdmin && vdr === "vp" && (
          <Impersonation userRole={authUser.roles} vrm={props.vrm} />
        )}
        <OverlayProvider>
          <BatteryGradeSlideout
            isOpen={isSlideoutOpen}
            onClose={handleCloseBatteryGradeSlideout}
            data={{
              condition:
                vehicleDetails?.additionalInformation?.evBattery
                  ?.batteryCondition,
              reportUrl: vehicleDetails?.batteryReport.href,
            }}
            analyticsData={analyticsData}
          />
        </OverlayProvider>
        {hpiError && (
          <ResponsiveLayout
            height="0px"
            css={`
              top: ${props.fromSearch === "true" ? "40px" : "0px"};
            `}
          >
            <HPIErrorBanner closeBanner={() => setHpiError(false)} />
          </ResponsiveLayout>
        )}
        {props.fromSearch === "true" && (
          <Box backgroundColor="#080f22">
            <ResponsiveLayout>
              <LinkButton
                text="Back to Results"
                variant="tertiary"
                icon="arrow-left-solid"
                iconAlignment="left"
                iconColor="white"
                px="0px"
                shouldOpenInNewWindow={false}
                analytics={{
                  eventLabel: "back-to-results",
                  eventAction: "navigate",
                }}
                css={`
                  color: white;
                  cursor: pointer;
                  text-decoration: underline;
                  font-size: 14px;
                  font-weight: normal;
                  max-width: 1080px;
                  display: flex;
                  flex-direction: row;
                  justify-content: flex-start;
                `}
                link={`${
                  process.env.auctionViewUrl
                }buyer/facetedsearch?${getStringFrom(
                  props.resolvedUrl,
                  "searchQuery="
                )}`}
              />
            </ResponsiveLayout>
          </Box>
        )}
        {isPrintServiceUnavailable && (
          <Banner
            variant="warning"
            title="Printing and Exporting Services Unavailable"
            primaryLinkLabel="Refresh"
            secondaryLinkLabel="Dismiss"
            primaryLinkAction={router.reload}
            secondaryLinkAction={() =>
              dispatch(SET_IS_PRINT_SERVICE_UNAVAILABLE(false))
            }
            displayMode="overlay"
            paddingX={60}
            paddingY={
              viewportWidth === "mobile" || viewportWidth === "tablet"
                ? 130
                : 80
            }
          >
            <Typography margin={0} variant="body">
              We were unable to print or export your selected vehicles. Please
              refresh the page to try again.
            </Typography>
          </Banner>
        )}
        {isLoadingFile && (
          <Snackbar
            title="Your download will open shortly in a new tab."
            verticalPosition="bottom"
            actionLabel="Dismiss"
            displayTime={10000}
            action={handleCloseSnackBar}
            onClose={handleCloseSnackBar}
            paddingY="20px"
            paddingX={viewportWidth === "desktop" ? "90px" : "60px"}
          />
        )}
        <BackToResultsStrip vrm={props.vrm} />
        {isVPSoldSourcePage &&
          ((vehicleDetails?.isLatestVersion &&
            vehicleDetails?.hasPreviousVersion) ||
            !vehicleDetails?.isLatestVersion) && (
            <VersionNavigationBanner
              isLatestVersion={vehicleDetails?.isLatestVersion}
              soldDate={vehicleDetails?.soldDate}
            />
          )}
        <ImageGallery
          images={vehicleDetails?.images ?? []}
          activeImageIndex={activeImageIndex}
          setActiveImageIndex={setActiveImageIndex}
          activeImageIndexRef={activeImageIndexRef}
          overlaySlot={
            <VehicleInteraction
              isOnSale={isOnSale}
              vrm={props.vrm}
              lotNumber={vehicleDetails?.lotNumber}
              saleCode={vehicleDetails?.saleDetails.saleCode}
              description={vehicleDetails?.description}
              lotNote={vehicleDetails?.lotNote}
              isTracked={isTracked}
              setIsTracked={setIsTracked}
              printSections={vehicleDetails?.printSections}
              isProvisional={vehicleDetails?.isProvisional}
              isFeatured={isFeaturedVehicle}
              setIsFeatured={setIsFeaturedVehicle}
              isAdmin={isAdmin}
              isFeatureVehicle={isFeatureVehicle}
              shouldRestrict={shouldRestrict}
              inventoryId={vehicleDetails?.inventoryData?.inventoryID}
              isToUseAuthZero={props.isToUseAuthZero}
              accessToken={accessToken}
            />
          }
          slideSpacing={SLIDE_SPACING}
        >
          {imagesWithDuplicates.map((image, index) => (
            <ClickableImage
              overflow="hidden"
              mr={`${SLIDE_SPACING}px`}
              key={image?.imageURI + index}
              width={[340, 600, 715]}
              height={[190, 335, 400]}
              imageURI={image?.imageURI}
              onClick={() => handleModalImage(imagesWithDuplicates, index)}
            />
          ))}
        </ImageGallery>
        {showImageZoomModal && (
          <ImageZoomModal
            activeImageIndex={activeImageIndex}
            setActiveImageIndex={setActiveImageIndex}
            activeImageIndexRef={activeImageIndexRef}
            imagesWithDuplicates={imagesWithDuplicates}
            images={vehicleDetails?.images}
            setShowImageZoomModal={setShowImageZoomModal}
          />
        )}
        <StyledResponsiveLayout
          display="flex"
          py={5}
          flexWrap="wrap"
          alignItems="flex-start"
        >
          <WarrantedMileage
            mileCount={vehicleDetails?.mileage}
            mileageWarranted={vehicleDetails?.mileageWarranted}
          />
          <Owners ownersCount={vehicleDetails?.owners} />
          <ConditionReport
            grade={vehicleDetails?.grade}
            anchorLink={vehicleDetails?.conditionReport.href}
          />
          {vehicleDetails?.additionalInformation?.evBattery?.isApplicable && (
            <BatteryReport
              grade={
                vehicleDetails?.additionalInformation?.evBattery
                  ?.batteryCondition
              }
              onSlideoutOpen={handleOpenBatteryGradeSlideout}
            />
          )}
          <MechanicalReport
            reportType={vehicleDetails?.mechanicalReport.type}
            anchorLink={vehicleDetails?.mechanicalReport.href ?? ""}
            categoryMapping={vehicleDetails?.assessment?.categoryMapping}
          ></MechanicalReport>
          {Boolean(vehicleDetails?.hpi) && (
            <HPISpecCheck
              starRating={vehicleDetails?.hpi.optionsRating}
              price={vehicleDetails?.hpi.optionsPrice}
              anchorLink="#hpi-spec-check-table"
            />
          )}
          <GuidePrice
            anchorLink="#guide-pricing"
            price={vehicleDetails?.prices?.CAPClean.value}
            soldDate={vehicleDetails?.soldDate}
          />
          {isSalvageVehicle && <SalvageDeclarations />}
          {isVPExceptSoldPageOrTransportTab && (
            <ValuationPrice
              anchorLink="#guide-pricing"
              price={bcaValuationResult}
              isBCAValuationLoading={isBCAValuationLoading}
              isBCAValuationAvailable={isBCAValuationAvailable}
            />
          )}
        </StyledResponsiveLayout>
        {(isOnSaleAndOwner || isOnSaleButNotOwner) &&
          !vehicleDetails?.isProvisional &&
          !isPostSaleVDP && (
            <GreySectionRow data-testid="sale-and-buying">
              <ResponsiveLayout
                display="flex"
                flexWrap={{
                  mobile: "wrap",
                  tablet: "wrap",
                  desktop: "nowrap",
                }}
                justifyContent="space-between"
                py={9}
              >
                <SaleInformation
                  biddingType={vehicleDetails?.biddingType}
                  saleName={vehicleDetails?.saleDetails?.name}
                  publishedName={vehicleDetails?.saleDetails?.publishedName}
                  startDate={vehicleDetails?.saleDetails?.startDate}
                  endDate={vehicleDetails?.saleDetails?.endDate}
                  sectionName={vehicleDetails?.saleDetails?.sectionName}
                  localSaleLocation={vehicleDetails?.saleDetails?.location}
                  saleLocation={vehicleDetails?.saleDetails?.saleLocation}
                  saleLocationUrl={vehicleDetails?.locationUrl}
                  lolCode={vehicleDetails?.saleDetails?.lolCode}
                  status={vehicleDetails?.saleDetails?.status}
                  numberOfLots={vehicleDetails?.saleDetails?.numberOfLots}
                  vehicleLocationDescription={
                    vehicleDetails?.additionalInformation
                      ?.vehicleLocationDescription
                  }
                />
                <BuyingOptions
                  biddingType={vehicleDetails?.biddingType}
                  biddingInfoUrl={vehicleDetails?.biddingInfoUrl}
                  placeBidUrl={vehicleDetails?.placeBidUrl}
                  acceptTermsAndConditionsUrl={
                    vehicleDetails?.acceptTermsAndConditionsUrl
                  }
                  vrm={props.vrm}
                  isTracked={isTracked}
                  setIsTracked={setIsTracked}
                  significantFields={vehicleDetails?.significantFields}
                  saleStartDate={vehicleDetails?.saleDetails?.startDate}
                  isOnSaleAndOwner={isOnSaleAndOwner}
                  inventoryId={vehicleDetails?.inventoryData?.inventoryID}
                  isToUseAuthZero={props.isToUseAuthZero}
                />
              </ResponsiveLayout>
            </GreySectionRow>
          )}
        <VehicleSpecification
          vehicleType={vehicleDetails?.vehicleType}
          color={vehicleDetails?.color}
          manufacturerColour={vehicleDetails?.manufacturerColour}
          fuel={vehicleDetails?.fuel}
          transmission={vehicleDetails?.transmission}
          doors={vehicleDetails?.doors}
          vin={vehicleDetails?.vin}
          logBook={vehicleDetails?.logBook}
          owners={vehicleDetails?.owners}
          registrationDate={vehicleDetails?.registrationDate}
          vatType={vehicleDetails?.vatType}
          serviceHistory={vehicleDetails?.serviceHistory}
          lastServiceDate={vehicleDetails?.lastServiceDate}
          MOTExpiryDate={vehicleDetails?.MOTExpiryDate}
          numberOfKeys={vehicleDetails?.additionalInformation?.numberOfKeys}
          dvsaMileage={
            vehicleDetails?.additionalInformation?.dvsaMileageEntries
          }
          notes={vehicleDetails?.serviceHistoryData?.notes}
          lastServiceMileage={
            vehicleDetails?.serviceHistoryData?.lastService?.mileage
          }
          serviceCount={vehicleDetails?.serviceHistoryData?.serviceCount}
          declaration={vehicleDetails?.declaration}
          co2Emissions={vehicleDetails?.additionalInformation?.emissions?.cO2}
          noxEmissions={vehicleDetails?.additionalInformation?.emissions?.nox}
          equipmentItems={vehicleDetails?.equipmentItems}
          advisoryItems={vehicleDetails?.additionalInformation?.advisoryItems}
          insuranceTotalLoss={vehicleDetails?.insuranceTotalLoss}
        />
        <ConditionAndMechanicalReports
          vehicleType={vehicleDetails?.vehicleType}
          mechanicalReport={vehicleDetails?.mechanicalReport}
          conditionReport={vehicleDetails?.conditionReport}
          grade={vehicleDetails?.grade}
          conditionSummaries={
            vehicleDetails?.assessment &&
            vehicleDetails?.assessment.conditionSummaries
          }
        />

        {Boolean(vehicleDetails?.hpi) && (
          <HPISpecCheckTable
            starRating={vehicleDetails?.hpi.optionsRating}
            basePrice={vehicleDetails?.hpi.vehiclePriceWithVat}
            optionsPrice={vehicleDetails?.hpi.optionsPrice}
            totalPrice={vehicleDetails?.hpi.totalPrice}
            specCheckOptions={vehicleDetails?.hpi.specCheckOptions}
          />
        )}
        {vehicleDetails?.userType !== "Viewer" && shouldShowPricing() && (
          <PricingGuide
            prices={vehicleDetails?.prices}
            soldDate={vehicleDetails?.soldDate}
            bcaValuation={bcaValuationResult}
            setShowValuationModal={setShowValuationModal}
            isBCAValuationLoading={isBCAValuationLoading}
            isBCAValuationAvailable={isBCAValuationAvailable}
          />
        )}
        {showValuationModal && (
          <ValuationModal
            viewportWidth={viewportWidth}
            setShowValuationModal={setShowValuationModal}
          />
        )}
        <UsabillaContainer>
          <div
            style={{
              width: "100%",
              maxWidth: 1080,
              margin: "0 auto",
              paddingTop: "25px",
            }}
            ub-in-page="5fa290dc94811e626a7fcceb"
          />
        </UsabillaContainer>
        <LotNavigationBanner
          navigation={vehicleDetails?.navigation}
          description={vehicleDetails?.description}
        />
      </FeatureFlagProvider>
    </Layout>
  );
};

export default VehicleDetailsAuth0;
